/**
 * Parse mixed invite input (emails and phone numbers) and decide delivery.
 * Phone numbers are normalized to E.164. Never treat a phone as an email.
 */

export type InviteRecipient =
  | { kind: "email"; email: string }
  | { kind: "phone"; phone: string }
  | { kind: "invalid"; raw: string; reason: string };

export type InviteDelivery = {
  kind: "email" | "phone";
  email: string | null;
  phone: string | null;
  existingUserId: string | null;
  notifyInApp: boolean;
  sendEmail: boolean;
  sendSms: boolean;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function digitsOnly(value: string): string {
  return value.replace(/\D/g, "");
}

/**
 * Normalize a phone to E.164. Bare 10-digit numbers are treated as US (+1).
 * Values that already start with + must include a country code (11-15 digits).
 * Returns null if the value is too short or too long to be a phone number.
 */
export function normalizePhoneE164(phone: string): string | null {
  const trimmed = phone.trim();
  const digits = digitsOnly(trimmed);
  const hasPlus = trimmed.startsWith("+");
  if (hasPlus) {
    if (digits.length >= 11 && digits.length <= 15) return `+${digits}`;
    return null;
  }
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  if (digits.length >= 11 && digits.length <= 15) return `+${digits}`;
  return null;
}

export function isEmailAddress(value: string): boolean {
  return EMAIL_RE.test(value.trim());
}

export function parseInviteToken(raw: string): InviteRecipient {
  const value = raw.trim();
  if (!value) {
    return { kind: "invalid", raw, reason: "empty" };
  }
  if (value.includes("@")) {
    const email = value.toLowerCase();
    if (!isEmailAddress(email)) {
      return { kind: "invalid", raw: value, reason: "invalid email" };
    }
    return { kind: "email", email };
  }
  const phone = normalizePhoneE164(value);
  if (!phone) {
    return { kind: "invalid", raw: value, reason: "invalid phone" };
  }
  return { kind: "phone", phone };
}

/** Split a textarea / comma list into tokens and classify each. */
export function parseInviteList(input: string): InviteRecipient[] {
  return input
    .split(/[,\n]/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map(parseInviteToken);
}

export function uniqueValidRecipients(values: string[]): Array<
  Extract<InviteRecipient, { kind: "email" | "phone" }>
> {
  const seen = new Set<string>();
  const out: Array<Extract<InviteRecipient, { kind: "email" | "phone" }>> = [];
  for (const value of values) {
    const parsed = parseInviteToken(value);
    if (parsed.kind === "invalid") continue;
    const key = parsed.kind === "email" ? `e:${parsed.email}` : `p:${parsed.phone}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(parsed);
  }
  return out;
}

/**
 * Existing accounts get an in-app (and push) notification and should not
 * depend on SMS. Email invites still send email so that path keeps working.
 * New phone numbers get SMS. New emails get email.
 */
export function planInviteDelivery(
  recipient: Extract<InviteRecipient, { kind: "email" | "phone" }>,
  existingUserId: string | null,
): InviteDelivery {
  if (recipient.kind === "email") {
    return {
      kind: "email",
      email: recipient.email,
      phone: null,
      existingUserId,
      notifyInApp: Boolean(existingUserId),
      sendEmail: true,
      sendSms: false,
    };
  }
  return {
    kind: "phone",
    email: null,
    phone: recipient.phone,
    existingUserId,
    notifyInApp: Boolean(existingUserId),
    sendEmail: false,
    sendSms: !existingUserId,
  };
}

export function formatInviteSendError(
  delivered: number,
  failures: string[],
): string | null {
  if (failures.length === 0) return null;
  if (delivered === 0) {
    return `Invites did not send: ${failures.join("; ")}`;
  }
  return `${delivered} invite${delivered === 1 ? "" : "s"} sent, but ${failures.length} failed: ${failures.join("; ")}`;
}
